/**
 * Пакетное одобрение недели — Б5.4, docs/phase-1/05-ui.md §6, макет bodies/Batch.html.
 *
 * ЭКРАН ПОКАЗЫВАЕТ ПОВЕДЕНИЕ БЭКЕНДА, А НЕ СВОЁ ПРЕДСТАВЛЕНИЕ О НЁМ. Источник —
 * `services/backend/src/domain/batch-approve.ts`; отсюда четыре вещи, которые нельзя
 * упростить в интерфейсе:
 *
 * 1. ДВЕ ФАЗЫ. Сначала предпроверка ВСЕХ элементов без единой записи, и только потом
 *    одобрение по одному. Поэтому у экрана два разных отказа с разным смыслом: до отправки —
 *    наша валидация уровня 1 по каждому посту отдельно (одна блокирующая ошибка отменяет
 *    пакет целиком, и виновник назван поимённо); после отправки — `409 state_conflict` с
 *    `details.blocked[]`, где бэкенд перечисляет все не прошедшие предпроверку посты сразу.
 *    В обоих случаях не одобрен ни один пост, и это сказано словами.
 * 2. ОТВЕТ 200 НЕ ЗНАЧИТ «ВЕСЬ ПАКЕТ ОДОБРЕН». Он несёт `results[]` поэлементно:
 *    `approved` с `publication_id`, `failed` с кодом отказа, `skipped` у хвоста. Экран
 *    рисует ровно это построчно — частичный успех показывается как частичный успех.
 *    Свести его к «пакет не прошёл» значило бы соврать: часть постов уже назначена на слоты
 *    и будет опубликована.
 * 3. ПАКЕТ ОСТАНАВЛИВАЕТСЯ НА ПЕРВОМ ОТКАЗЕ. Самый вероятный отказ — недоступность демона
 *    подписи, и следующие попытки дали бы то же самое, каждая со своей осиротевшей работой.
 *    Поэтому у хвоста статус `skipped` = «не пробовали», и рядом сказано, что делать:
 *    отправить хвост заново, когда причина устранена. Автоповтора нет и не будет.
 * 4. ОТМЕНИТЬ УЖЕ ОДОБРЕННОЕ ЭКРАН НЕ ПРЕДЛАГАЕТ. Выпущенный ключ не отзывается задним
 *    числом; снятие со слота — отдельное решение человека на экране черновика.
 *
 * СЛОТ У КАЖДОГО ПОСТА СВОЙ, И ЭТО РЕШЕНО ЧЕСТНО. Один `slot_at` на весь пакет невозможен:
 * в расписании один слот занимает ровно один пост (`ux_slot_draft`), и бэкенд отдельно ловит
 * два элемента с одним слотом как ошибку формы пакета. Поэтому общий `SlotPicker` выбирает
 * НАЧАЛО очереди, а не слот пакета: свободные слоты расписания, начиная с выбранного, в
 * хронологическом порядке раздаются выбранным постам в порядке карточек (порядок двигают
 * кнопки ↑/↓). Занятые слоты в очередь не попадают вовсе — они пропускаются автоматически,
 * как и нарисовано в макете. Если свободных слотов меньше, чем постов, пакет не отправляется
 * и названы посты, которым слота не хватило. Каждый `slot_at` уходит в тело запроса ДОСЛОВНО
 * из ответа расписания (09 §9.4): собранный на клиенте момент отвергнет
 * `CHECK (slot_at_ms % 1000 = 0)` уже после нажатия.
 *
 * КОМПАКТНОЕ ПРЕВЬЮ — ТОТ ЖЕ `MessagePreview`, ПОДРЕЗАННЫЙ CSS. Второго рендера поста в
 * приложении нет: иначе в пакете и на экране черновика человек утверждал бы два разных
 * текста. Карточка клипует превью по высоте и разворачивает его кнопкой.
 *
 * ПАКЕТ — ОДНОГО БРЕНДА И ОДНОГО КАНАЛА (2026-10-25, изоляция брендов). Тело несёт
 * `expected_brand` (бренд экрана) и `expected_chat_id` (канал из плашки режима) НА ПАКЕТ, и
 * бэкенд сверяет их с каждым элементом до первой подписи (`BatchApproveBodySchema` контрактов).
 * Канал не подтверждён — пакета нет: назвать бэкенду нечего. Пост чужого бренда в пакете
 * экран не пропускает и сам — бэклог бренда его не отдал бы, но отказ всего пакета на
 * бэкенде из-за одного поста хуже названного поимённо виновника здесь.
 *
 * РАЗБОР ОТВЕТА ПАКЕТА ЖИВЁТ ЗДЕСЬ, А НЕ В `contracts`, — вынужденно: схемы
 * `BatchApproveResponse` в `packages/contracts/src/api/ui.ts` нет, а завести её значит
 * править чужой пакет. Разбор поэтому ручной и строгий: неузнанное тело не рисуется
 * «наполовину», а становится ошибкой с прямым текстом.
 */

import { type DraftState, isoToMs, msToIso, nowMs } from '@smm/contracts';
import {
  BATCH_APPROVE_MAX_ITEMS,
  type BatchApproveBody,
  type BatchApproveItem,
  posterDateLabel,
  posterRedatePrompt,
  postLabel,
} from '@smm/contracts/schemas';
import { type JSX, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'wouter';
import { ApiRequestError, get, post } from '../api/client.ts';
import {
  type BacklogItem,
  type BacklogResponse,
  type PublicationDispatch,
  type RenderModel,
  RenderModelSchema,
  type RequestChangesResponse,
  type ScheduleResponse,
  type ScheduleSlot,
  TransportPolicySchema,
} from '../api/types.ts';
import {
  CHANNEL_BRAND_UNKNOWN_TEXT,
  CHANNEL_LOADING_TEXT,
  CHANNEL_UNKNOWN_TEXT,
  channelMissingText,
  readyChatId,
  useModeChannel,
} from '../api/useModeChannel.ts';
import { useResource } from '../api/useResource.ts';
import { type BrandsState, brandBannedOrgs, useBrands } from '../brand.tsx';
import { shortMsk } from '../components/format.ts';
import { IconCheck, IconWarn } from '../components/icons.tsx';
import { Shell } from '../components/Shell.tsx';
import { StatusPill } from '../components/StatusPill.tsx';
import { useShellSettings } from '../components/shellSettings.ts';
import { LengthMeter } from '../preview/LengthMeter.tsx';
import { MessagePreview } from '../preview/MessagePreview.tsx';
import { validateDraft } from '../preview/validation.ts';
import styles from './BatchPage.module.css';
import { bannedOrgGate } from './bannedOrgs.ts';
import type { UiIssue } from './contracts.ts';
import { HoldToConfirm } from './decisions/HoldToConfirm.tsx';
import { SlotPicker, slotLabel } from './decisions/SlotPicker.tsx';
import { posterSlotMismatch, visualIssues, visualViewOf } from './visual/visualState.ts';

/** Потолок пакета — общий с бэкендом (`BATCH_APPROVE_MAX_ITEMS` контрактов). */
const BATCH_MAX_ITEMS = BATCH_APPROVE_MAX_ITEMS;

/** Горизонт расписания — тот же, что у SlotPicker (09 §9.4). */
const HORIZON_MS = 28 * 24 * 60 * 60 * 1000;

/**
 * Статусы, из которых одобрение законно, — зеркало `APPROVABLE` из domain/approve.ts.
 * Показать здесь только «ожидает» было бы удобнее, но тогда пост, упавший при публикации,
 * нельзя было бы вернуть в неделю пакетом, хотя бэкенд это разрешает.
 */
const APPROVABLE: ReadonlySet<DraftState> = new Set<DraftState>([
  'pending_review',
  'approved',
  'failed',
]);

/** Галочка по умолчанию стоит только у «ожидает» — как в макете. */
const DEFAULT_CHECKED: DraftState = 'pending_review';

/** Пояснение к карточке, которая в пакете законна, но уже что-то занимает или уже упала. */
const STATUS_NOTE: Partial<Record<DraftState, string>> = {
  approved: 'уже занимает слот — повторное одобрение выпустит новый ключ на новый слот',
  failed: 'публикация не состоялась — одобрение назначит пост на новый слот',
};

const SCHEMA_ISSUE: UiIssue = {
  level: 'blocking',
  code: 'render_model_invalid',
  message:
    'Версия черновика не соответствует текущему алгоритму канонизации — обратитесь к инженеру.',
};

const loadIssue = (e: unknown): UiIssue => ({
  level: 'blocking',
  code: 'render_model_unavailable',
  message: `Версия черновика не загрузилась: ${e instanceof Error ? e.message : String(e)}`,
});

/**
 * Подпись черновика — та же, что в бэклоге (`BacklogAside`): её считает бэкенд
 * (`read-model.ts` → `backlogTitle`) из темы или первой строки поста и уже сам откатывается
 * к имени фикстуры и к номеру поста. У постов писательского происхождения `fixture_id`
 * пуст, и до этой правки человек видел в пакете восемь шестнадцатеричных знаков вместо
 * названия поста (F-089, K-011).
 */
const titleOf = (item: BacklogItem): string => {
  const t = item.title.trim();
  return t === '' ? postLabel(item.post_no) : t;
};

/* ── разбор ответа пакета ─────────────────────────────────────────────────── */

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

export interface BatchItemResult {
  readonly draft_id: string;
  readonly version_no: number;
  readonly slot_at: string;
  readonly status: 'approved' | 'failed' | 'skipped';
  readonly publication_id: string | null;
  readonly error: { readonly code: string; readonly message: string } | null;
}

export interface BatchBlockedItem {
  readonly draft_id: string;
  readonly code: string;
  readonly message: string;
}

const asString = (v: unknown): string | null => (typeof v === 'string' ? v : null);

function parseItemResult(raw: unknown): BatchItemResult | null {
  if (!isRecord(raw)) return null;
  const draftId = asString(raw['draft_id']);
  const slotAt = asString(raw['slot_at']);
  const versionNo = raw['version_no'];
  const status = raw['status'];
  if (draftId === null || slotAt === null || typeof versionNo !== 'number') return null;
  if (status !== 'approved' && status !== 'failed' && status !== 'skipped') return null;
  const err = raw['error'];
  const code = isRecord(err) ? asString(err['code']) : null;
  const message = isRecord(err) ? asString(err['message']) : null;
  return {
    draft_id: draftId,
    version_no: versionNo,
    slot_at: slotAt,
    status,
    publication_id: asString(raw['publication_id']),
    error: code === null || message === null ? null : { code, message },
  };
}

/** `null` — тело не то, что обещано контрактом. Половину списка не рисуем принципиально. */
export function parseBatchResults(body: unknown): readonly BatchItemResult[] | null {
  if (!isRecord(body)) return null;
  const raw = body['results'];
  if (!Array.isArray(raw)) return null;
  const out: BatchItemResult[] = [];
  for (const one of raw) {
    const parsed = parseItemResult(one);
    if (parsed === null) return null;
    out.push(parsed);
  }
  return out;
}

/** `details.blocked[]` отказа предпроверки — все виноватые посты сразу, а не первый. */
export function parseBlocked(details: unknown): readonly BatchBlockedItem[] | null {
  if (!isRecord(details)) return null;
  const raw = details['blocked'];
  if (!Array.isArray(raw)) return null;
  const out: BatchBlockedItem[] = [];
  for (const one of raw) {
    if (!isRecord(one)) return null;
    const draftId = asString(one['draft_id']);
    const code = asString(one['code']);
    const message = asString(one['message']);
    if (draftId === null || code === null || message === null) return null;
    out.push({ draft_id: draftId, code, message });
  }
  return out.length === 0 ? null : out;
}

/* ── загрузка моделей рендера ─────────────────────────────────────────────── */

interface LoadedCard {
  readonly item: BacklogItem;
  /** `null` — версия не загрузилась или не сошлась со схемой; тогда в issues лежит блокирующая. */
  readonly model: RenderModel | null;
  readonly issues: readonly UiIssue[];
}

/**
 * Пакет требует канонического содержания каждого поста: без него нечего валидировать, нечего
 * показывать и нечем заполнять `expected_content_hash`. Бэклог его не отдаёт (BacklogItemSchema),
 * поэтому модель рендера тянется по каждой карточке отдельно.
 *
 * Зависимость эффекта — сам массив `items`, а не строка-ключ: он приходит из `useResource`
 * и меняет тождество ровно тогда, когда бэклог перезапрошен, то есть после решения. Это и
 * нужно: после пакета содержимое карточек обязано перечитаться.
 *
 * ПРОВЕРКИ TELEGRAM СЧИТАЮТСЯ ПОВЕРХ ЗАГРУЗКИ, а не внутри неё: им нужна политика бренда
 * (`richAlways`, §9.3 партии 19.09) — без неё длинный пост получал бы совет «уберите N
 * кодпоинтов, чтобы вернуть карточку», которой при включённой политике не будет. Политика
 * приезжает своим запросом, и её приход пересчитывает проверки, а не перезапрашивает посты.
 * `loaded` — сырые карточки: их тождество меняется только с перезагрузкой, и сброс галочек
 * держится за него, а не за пересчёт.
 *
 * Гейт пометки организаций, запрещённых в РФ (`bannedOrgGate`, 2026-11-02), считается там же и по
 * той же причине: ему нужен флаг бренда из списка брендов, а список приезжает своим запросом.
 * Без гейта здесь пакет недели обходил бы страховку, которая закрывает одобрение на экране поста,
 * и бэкенд отбил бы такие посты только по одному внутри пакета.
 */
function useCards(
  items: readonly BacklogItem[],
  richAlways: boolean | undefined,
  brands: BrandsState,
): {
  readonly cards: readonly LoadedCard[];
  readonly loaded: readonly LoadedCard[];
  readonly loading: boolean;
} {
  const [state, setState] = useState<{ cards: readonly LoadedCard[]; loading: boolean }>({
    cards: [],
    loading: items.length > 0,
  });

  useEffect(() => {
    if (items.length === 0) {
      setState({ cards: [], loading: false });
      return () => undefined;
    }
    const ac = new AbortController();
    setState((s) => ({ cards: s.cards, loading: true }));
    Promise.all(
      items.map(async (item): Promise<LoadedCard> => {
        const path = `/api/v1/drafts/${item.draft_id}/versions/${String(item.current_version)}`;
        try {
          const body = await get<unknown>(path, ac.signal);
          const parsed = RenderModelSchema.safeParse(body);
          if (!parsed.success) return { item, model: null, issues: [SCHEMA_ISSUE] };
          // Гейт визуала входит в тот же список, что и правила Telegram: без него пакетное
          // одобрение недели обошло бы решение по картинкам целиком — «одобрить неделю»
          // стало бы способом не решать по ним вовсе.
          return { item, model: parsed.data, issues: visualIssues(parsed.data) };
        } catch (e) {
          return { item, model: null, issues: [loadIssue(e)] };
        }
      }),
    ).then((cards) => {
      if (ac.signal.aborted) return;
      setState({ cards, loading: false });
    });
    return () => {
      ac.abort();
    };
  }, [items]);

  const cards = useMemo(
    () =>
      state.cards.map(
        (c): LoadedCard =>
          c.model === null
            ? c
            : {
                ...c,
                issues: [
                  ...validateDraft(c.model.canonical, { richAlways }),
                  ...c.issues,
                  ...bannedOrgGate(c.model, brandBannedOrgs(brands, c.model.brand)),
                ],
              },
      ),
    [state.cards, richAlways, brands],
  );
  return { cards, loaded: state.cards, loading: state.loading };
}

/* ── карточка поста ───────────────────────────────────────────────────────── */

/**
 * Чип визуала: §6.5 дословно — «пакетный экран на неделю показывает, у каких постов есть
 * картинка». Он же делает долю видимой глазом на недельном горизонте, а не только числом на
 * «Мониторинге»: семь карточек подряд с «с картинкой» — это и есть вырождение механизма.
 *
 * Состояния «вердикта нет» чипа не получают вовсе: у версий до 0015 сказать нечего, и
 * пустой чип соврал бы про измеренное.
 */
function VisualChip({ model }: { model: RenderModel }): JSX.Element | null {
  const view = visualViewOf(model);
  if (view.state === 'unset') return null;
  if (view.state === 'proposed') {
    return (
      <span className={`${styles['visualChip']} ${styles['visualChipOpen']}`}>
        картинка не решена
      </span>
    );
  }
  return (
    <span className={styles['visualChip']}>
      {view.state === 'attached' ? 'с картинкой' : 'картинки нет'}
    </span>
  );
}

function CardMark({ issues }: { issues: readonly UiIssue[] }): JSX.Element {
  const blocking = issues.filter((i) => i.level === 'blocking');
  const warnings = issues.filter((i) => i.level === 'warning');
  const first = blocking[0];
  if (first !== undefined) {
    return (
      <span className={`${styles['mark']} ${styles['markErr']}`}>
        <IconWarn /> {first.message}
      </span>
    );
  }
  if (warnings.length > 0) {
    return (
      <span className={`${styles['mark']} ${styles['markWarn']}`}>
        <IconWarn />{' '}
        {warnings.length === 1 ? warnings[0]?.message : `предупреждений: ${warnings.length}`} —
        можно утверждать
      </span>
    );
  }
  return (
    <span className={`${styles['mark']} ${styles['markOk']}`}>
      <IconCheck /> валиден
    </span>
  );
}

interface BatchCardProps {
  readonly card: LoadedCard;
  readonly brand: string;
  readonly checked: boolean;
  readonly slot: ScheduleSlot | null;
  readonly first: boolean;
  readonly last: boolean;
  readonly onToggle: () => void;
  readonly onMove: (by: -1 | 1) => void;
}

function BatchCard({
  card,
  brand,
  checked,
  slot,
  first,
  last,
  onToggle,
  onMove,
}: BatchCardProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const { item, model, issues } = card;
  const blocked = issues.some((i) => i.level === 'blocking');
  const title = titleOf(item);
  const note = STATUS_NOTE[item.status];
  const checkboxId = `batch-${item.draft_id}`;

  return (
    <div
      className={`card-flat ${styles['card']} ${blocked ? styles['cardBlocked'] : ''}`}
      data-testid="batch-card"
    >
      <input
        id={checkboxId}
        type="checkbox"
        className={styles['check']}
        checked={checked}
        onChange={onToggle}
        aria-label={`включить в пакет: ${title}`}
      />

      <div className={styles['body']}>
        <div className={styles['titleRow']}>
          <StatusPill status={item.status} />
          <label className={`h3 ${styles['title']}`} htmlFor={checkboxId}>
            {title}
          </label>
          <span className="chip-plain">v{item.current_version}</span>
          {model === null ? null : <VisualChip model={model} />}
          <div style={{ flex: 1 }} />
          <div className={styles['moves']}>
            <button
              type="button"
              className={styles['move']}
              disabled={first}
              aria-label={`выше в очереди слотов: ${title}`}
              onClick={() => {
                onMove(-1);
              }}
            >
              ↑
            </button>
            <button
              type="button"
              className={styles['move']}
              disabled={last}
              aria-label={`ниже в очереди слотов: ${title}`}
              onClick={() => {
                onMove(1);
              }}
            >
              ↓
            </button>
          </div>
        </div>

        {note === undefined ? null : <div className="micro">{note}</div>}

        {model === null ? (
          <div className="val val-err">
            <IconWarn />
            <span>{issues[0]?.message ?? 'Версия черновика недоступна.'}</span>
          </div>
        ) : (
          <>
            <div className={open ? styles['previewOpen'] : styles['preview']}>
              <MessagePreview
                canonical={model.canonical}
                mediaRef={model.media_ref}
                documentRef={model.document_ref ?? null}
                mode="expected"
                theme="light"
                brand={brand}
              />
            </div>
            <button
              type="button"
              className={styles['linkBtn']}
              onClick={() => {
                setOpen((v) => !v);
              }}
            >
              {open ? 'Свернуть превью' : 'Развернуть превью'}
            </button>
            <LengthMeter canonical={model.canonical} />
          </>
        )}

        <div className={styles['footRow']}>
          <CardMark issues={issues} />
          <div style={{ flex: 1 }} />
          <Link
            href={`/b/${encodeURIComponent(brand)}/draft/${encodeURIComponent(item.draft_id)}`}
            className={styles['linkBtn']}
          >
            Открыть черновик
          </Link>
          {slot === null ? (
            <span className={`mono ${styles['slotNone']}`}>→ —</span>
          ) : (
            <span className={`mono num ${styles['slotTag']}`}>→ {slotLabel(slot.slot_local)}</span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── результаты отправки ──────────────────────────────────────────────────── */

interface Sent {
  readonly draft_id: string;
  readonly title: string;
  /** Момент слота в зоне бренда — тот же, что показан в очереди и на карточке. */
  readonly slot_local: string | null;
}

const RESULT_PILL: Readonly<Record<BatchItemResult['status'], string>> = {
  approved: 'pill st-ok',
  failed: 'pill st-err',
  skipped: 'pill st-hold',
};

const RESULT_LABEL: Readonly<Record<BatchItemResult['status'], string>> = {
  approved: 'одобрен',
  failed: 'отказ',
  skipped: 'не пробовали',
};

function ResultsPanel({
  results,
  sent,
}: {
  readonly results: readonly BatchItemResult[];
  readonly sent: readonly Sent[];
}): JSX.Element {
  const nameOf = (draftId: string): string =>
    sent.find((s) => s.draft_id === draftId)?.title ?? 'пост не из этого пакета';
  /**
   * Слот подписывается ровно так же, как в очереди и на карточке, — в зоне бренда. До этой
   * правки здесь печатался сырой `slot_at` из ответа, то есть UTC без пометки о зоне:
   * человек читал «17:00» там, где пост уйдёт в 20:00 (F-090). Если локального момента для
   * результата нет, показывается UTC — но названный UTC, а не молча.
   */
  const slotOf = (r: BatchItemResult): string => {
    const local = sent.find((s) => s.draft_id === r.draft_id)?.slot_local ?? null;
    return local === null ? shortMsk(r.slot_at) : slotLabel(local);
  };
  const approved = results.filter((r) => r.status === 'approved').length;
  const failed = results.filter((r) => r.status === 'failed').length;
  const skipped = results.filter((r) => r.status === 'skipped').length;

  return (
    <div className={`card-flat ${styles['sideCard']}`} data-testid="batch-results">
      <div className="h3">Результат пакета</div>
      <div className="meta">
        одобрено {approved} · отказ {failed} · не пробовали {skipped}
      </div>

      <div className="stack" style={{ gap: 2 }}>
        {results.map((r) => (
          <div key={r.draft_id} className={`vrow ${styles['resultRow']}`}>
            <span className={RESULT_PILL[r.status]}>{RESULT_LABEL[r.status]}</span>
            <div className={styles['resultBody']}>
              <span className="small">{nameOf(r.draft_id)}</span>
              {r.status === 'approved' ? (
                <span className="meta mono">
                  публикация {r.publication_id ?? '—'} · слот {slotOf(r)}
                </span>
              ) : null}
              {r.status === 'failed' && r.error !== null ? (
                <span className="meta" style={{ color: 'var(--red-ink)' }}>
                  код {r.error.code} — {r.error.message}
                </span>
              ) : null}
              {r.status === 'skipped' ? (
                <span className="meta">до этого поста пакет не дошёл</span>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      {failed === 0 ? (
        <div className="notice">
          Все посты пакета получили свой ключ, действительный каждый до своего слота.
        </div>
      ) : (
        <div className="val val-warn">
          <IconWarn />
          <span>
            Пакет остановился на первом отказе: одобренные посты остаются одобренными — ключ, уже
            выпущенный и подписанный, не отзывается задним числом. Снять пост со слота можно только
            отдельным решением на экране черновика. Устраните причину отказа и отправьте хвост
            пакета заново.
          </span>
        </div>
      )}
    </div>
  );
}

/* ── экран ────────────────────────────────────────────────────────────────── */

export function BatchPage({ brand }: { brand: string }): JSX.Element {
  return (
    <Shell brand={brand} active="backlog">
      <BatchScreen brand={brand} />
    </Shell>
  );
}

/**
 * Тело пакетного экрана — ВНУТРИ шапки, отдельным компонентом. Шапка сообщает об изменённых
 * «Настройках» и режиме публикаций счётчиком `rev` через контекст (`shellSettings.ts`), а
 * контекст виден только тем, кто рисуется ПОД `Shell`: пока хуки жили в самом `BatchPage`,
 * рядом с шапкой, политика бренда читалась один раз, и совет «уберите N кодпоинтов» у длинного
 * поста оставался или пропадал не по правде до перезагрузки (ревью 19.09.2026, ui-9).
 */
function BatchScreen({ brand }: { brand: string }): JSX.Element {
  const backlog = useResource<BacklogResponse>(
    `/api/v1/backlog?brand=${encodeURIComponent(brand)}&limit=200`,
  );

  // Диапазон считается один раз: строка пути — зависимость эффекта загрузки (см. SlotPicker).
  const [range] = useState(() => {
    const from = nowMs();
    return { from: msToIso(from), to: msToIso(from + HORIZON_MS) };
  });
  const schedule = useResource<ScheduleResponse>(
    `/api/v1/schedule?brand=${encodeURIComponent(brand)}&from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
  );
  // Режим публикаций решает канал всего пакета и уходит в подпись каждого ключа — та же
  // причина, что в `ApproveDialog`: показанное человеку и подписанное обязаны совпадать.
  // Режим — этого бренда (docs/multi-brand.md §3): у соседнего бренда он может быть другим.
  const dispatch = useResource<PublicationDispatch>(
    `/api/v1/publication-dispatch?brand=${encodeURIComponent(brand)}`,
  );
  const mode = dispatch.data?.state;
  // Канал текущего режима на весь пакет: пустое поле режима в конфиге — отказ бэкенда на
  // ПЕРВОМ же посте, то есть пакет, остановившийся ни на чём. Говорим до удержания кнопки.
  const { channel, refetch: refetchChannel } = useModeChannel(brand);

  const approvable = useMemo(
    () => (backlog.data ?? []).filter((i) => APPROVABLE.has(i.status)),
    [backlog.data],
  );
  // Политика бренда — только для проверок карточек (см. `useCards`); чужой ответ значит
  // «неизвестна», и тогда совет про карточку не подавляется — как на экране черновика.
  const policyRes = useResource<unknown>(
    `/api/v1/transport-policy?brand=${encodeURIComponent(brand)}`,
  );
  const policyRich = useMemo((): boolean | undefined => {
    const r = TransportPolicySchema.safeParse(policyRes.data);
    return r.success ? r.data.rich_always : undefined;
  }, [policyRes.data]);
  // Список брендов — ради флага разметки организаций, запрещённых в РФ (гейт в `useCards`).
  const brands = useBrands();
  const { cards, loaded, loading } = useCards(approvable, policyRich, brands);

  // «Настройки» или режим публикаций сменили в шапке — перечитать то, что от них зависит:
  // политику (проверки карточек), режим и канал режима (плашка режима и `expected_mode`
  // пакета). Посты не перезапрашиваются: пересчёт проверок идёт от политики (`useCards`).
  const { refetch: refetchPolicy } = policyRes;
  const { refetch: refetchDispatch } = dispatch;
  const { rev } = useShellSettings();
  const seenRev = useRef(rev);
  useEffect(() => {
    if (seenRev.current === rev) return;
    seenRev.current = rev;
    refetchPolicy();
    refetchDispatch();
    refetchChannel();
  }, [rev, refetchPolicy, refetchDispatch, refetchChannel]);

  const [order, setOrder] = useState<readonly string[]>([]);
  const [checked, setChecked] = useState<ReadonlySet<string>>(() => new Set<string>());
  const [startSlotId, setStartSlotId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<readonly BatchItemResult[] | null>(null);
  const [blocked, setBlocked] = useState<readonly BatchBlockedItem[] | null>(null);
  const [sent, setSent] = useState<readonly Sent[]>([]);

  // Состав пакета пересобирается при каждой перезагрузке карточек: после отправки часть
  // постов ушла в «запланирован», и держать на них галочку значило бы показывать пакет,
  // которого больше нет.
  useEffect(() => {
    setOrder(loaded.map((c) => c.item.draft_id));
    setChecked(
      new Set(loaded.filter((c) => c.item.status === DEFAULT_CHECKED).map((c) => c.item.draft_id)),
    );
  }, [loaded]);

  const ordered = useMemo(() => {
    const byId = new Map(cards.map((c): readonly [string, LoadedCard] => [c.item.draft_id, c]));
    const seen = new Set<string>();
    const out: LoadedCard[] = [];
    for (const id of order) {
      const card = byId.get(id);
      if (card !== undefined) {
        out.push(card);
        seen.add(id);
      }
    }
    for (const card of cards) if (!seen.has(card.item.draft_id)) out.push(card);
    return out;
  }, [cards, order]);

  const chosen = useMemo(
    () => ordered.filter((c) => checked.has(c.item.draft_id)),
    [ordered, checked],
  );

  /** Свободные будущие слоты в хронологическом порядке — та же выборка, что у SlotPicker. */
  const freeSlots = useMemo(() => {
    const now = nowMs();
    return (schedule.data?.slots ?? [])
      .filter((s) => s.state === 'free' && isoToMs(s.slot_at) > now)
      .toSorted((a, b) => isoToMs(a.slot_at) - isoToMs(b.slot_at));
  }, [schedule.data]);

  const startIndex = freeSlots.findIndex((s) => s.slot_id === startSlotId);
  const queue = startIndex < 0 ? [] : freeSlots.slice(startIndex);

  /**
   * СЛОТЫ ПОД ПЕРЕРИСОВКУ ДНЯ (docs/phase-2/10-posters.md): черновик → слот, под день которого
   * заказана перерисовка обложки. Заказ уводит черновик в `changes_requested`, то есть из
   * пакета, пока писатель не вернёт версию с новым днём; раздача по порядку отдала бы его слот
   * следующей карточке — и обложка, верная до нажатия, разошлась бы со слотом, а экран
   * предложил бы перерисовать и её. Поэтому слот придержан: его обходят все, кроме своего
   * черновика, а вернувшийся черновик встаёт ровно в него. Снятая с черновика галочка слот
   * отпускает. Это состояние экрана, не сервера: после перезагрузки раздача снова идёт по
   * порядку, и расхождения экран покажет заново.
   */
  const [held, setHeld] = useState<ReadonlyMap<string, string>>(() => new Map());

  const { assignment, heldAway } = useMemo(() => {
    const present = new Set(ordered.map((c) => c.item.draft_id));
    const chosenIds = new Set(chosen.map((c) => c.item.draft_id));
    const reserved = new Set<string>();
    for (const [draftId, slotId] of held) {
      if (!present.has(draftId) || chosenIds.has(draftId)) reserved.add(slotId);
    }
    const map = new Map<string, ScheduleSlot>();
    const used = new Set<string>();
    for (const card of chosen) {
      const slotId = held.get(card.item.draft_id);
      const slot = slotId === undefined ? undefined : queue.find((q) => q.slot_id === slotId);
      if (slot === undefined || used.has(slot.slot_id)) continue;
      map.set(card.item.draft_id, slot);
      used.add(slot.slot_id);
    }
    const rest = queue.filter((q) => !reserved.has(q.slot_id));
    let next = 0;
    for (const card of chosen) {
      if (map.has(card.item.draft_id)) continue;
      const slot = rest[next];
      next += 1;
      if (slot !== undefined) map.set(card.item.draft_id, slot);
    }
    // Слоты черновиков, которые ещё в пути от писателя: они свободны, но пакету не достаются.
    const away = freeSlots.filter((f) =>
      [...held].some(([draftId, slotId]) => slotId === f.slot_id && !present.has(draftId)),
    );
    return { assignment: map, heldAway: away };
  }, [chosen, ordered, queue, held, freeSlots]);

  /* ── что мешает отправить пакет ── */

  const blockers: { readonly key: string; readonly text: string }[] = [];
  if (chosen.length === 0) {
    blockers.push({ key: 'empty', text: 'Не выбрано ни одного поста.' });
  }
  if (chosen.length > BATCH_MAX_ITEMS) {
    blockers.push({
      key: 'too-many',
      text: `В пакете ${chosen.length} постов при потолке ${BATCH_MAX_ITEMS}. Снимите лишние галочки: каждый элемент — это вызов демона подписи и отдельный ключ.`,
    });
  }
  for (const card of chosen) {
    for (const issue of card.issues) {
      if (issue.level !== 'blocking') continue;
      blockers.push({
        key: `${card.item.draft_id}:${issue.code}`,
        text: `«${titleOf(card.item)}» — ${issue.message}`,
      });
    }
  }
  if (mode === undefined) {
    blockers.push({
      key: 'no-mode',
      text:
        dispatch.error === undefined
          ? 'Загружаем режим публикаций: без него неизвестно, в какой канал уйдёт пакет.'
          : 'Режим публикаций не загрузился — неизвестно, в какой канал уйдёт пакет. Обновите экран.',
    });
  }
  if (channel.kind === 'missing') {
    blockers.push({ key: 'no-channel', text: channelMissingText(mode) });
  } else if (channel.kind === 'unknown') {
    blockers.push({ key: 'unknown-brand', text: CHANNEL_BRAND_UNKNOWN_TEXT });
  } else if (channel.kind === 'failed' || channel.kind === 'malformed') {
    blockers.push({ key: 'channel-unknown', text: CHANNEL_UNKNOWN_TEXT });
  } else if (channel.kind === 'loading') {
    blockers.push({ key: 'channel-loading', text: CHANNEL_LOADING_TEXT });
  }
  for (const card of chosen) {
    if (card.model === null || card.model.brand === brand) continue;
    blockers.push({
      key: `foreign:${card.item.draft_id}`,
      text: `«${titleOf(card.item)}» — пост бренда ${card.model.brand}, а пакет собирается для ${brand}: одобрите его на экране своего бренда.`,
    });
  }
  if (startSlotId === null) {
    blockers.push({
      key: 'no-slot',
      text: 'Не выбран первый слот пакета: остальные раздаются по порядку от него.',
    });
  } else {
    const awayInQueue = heldAway.filter((h) => queue.includes(h)).length;
    for (const card of chosen) {
      if (assignment.has(card.item.draft_id)) continue;
      blockers.push({
        key: `no-free-slot:${card.item.draft_id}`,
        text:
          `«${titleOf(card.item)}» — свободного слота не хватило: постов ${chosen.length}, свободных слотов от выбранного ${queue.length}` +
          (awayInQueue > 0
            ? `, из них ${String(awayInQueue)} придержано под посты, которые ждут новой даты обложки.`
            : '.'),
      });
    }
  }

  // ДАТА ОБЛОЖКИ — ДЕНЬ СЛОТА (docs/phase-2/10-posters.md). Пакет раздаёт слоты по порядку,
  // и пост с обложкой «на вторник» может получить четверг; бэкенд отбил бы такой пакет на
  // предпроверке целиком. Экран видит это сам, до отправки, — и предлагает перерисовать дни
  // всех таких обложек под слоты пакета одним нажатием (переделки `redate:`, без модели).
  const posterMismatches = chosen.flatMap((card) => {
    const slot = assignment.get(card.item.draft_id);
    if (card.model === null || slot === undefined) return [];
    const m = posterSlotMismatch(visualViewOf(card.model), slot.slot_local);
    return m === null ? [] : [{ card, model: card.model, slot, ...m }];
  });
  for (const m of posterMismatches) {
    blockers.push({
      key: `poster-date:${m.card.item.draft_id}`,
      text: `«${titleOf(m.card.item)}» — на обложке ${posterDateLabel(m.posterDay)}, а слот пакета ${posterDateLabel(m.slotDay)}: обложка входит в подпись, и день нужно перерисовать.`,
    });
  }
  const [redating, setRedating] = useState(false);
  const redateAll = (): void => {
    if (redating || posterMismatches.length === 0) return;
    setRedating(true);
    setError(null);
    // По одной, а не пачкой: у каждой своя строка переделки и своя версия, и отказ одной не
    // имеет права молча отменить остальные — человек увидит, какая не ушла.
    const failed: string[] = [];
    const requested: { readonly draftId: string; readonly slotId: string }[] = [];
    const work = posterMismatches.reduce<Promise<void>>(
      (chain, m) =>
        chain.then(() =>
          post<RequestChangesResponse>(
            `/api/v1/drafts/${m.model.draft_id}/versions/${String(m.model.version_no)}/request-changes`,
            { prompt: posterRedatePrompt(m.slotDay), targets: ['visual'] },
          ).then(
            () => {
              requested.push({ draftId: m.model.draft_id, slotId: m.slot.slot_id });
            },
            (e: unknown) => {
              failed.push(
                `«${titleOf(m.card.item)}»: ${e instanceof Error ? e.message : String(e)}`,
              );
            },
          ),
        ),
      Promise.resolve(),
    );
    void work.finally(() => {
      setRedating(false);
      if (requested.length > 0) {
        setHeld((prev) => {
          const next = new Map(prev);
          for (const o of requested) {
            // Один слот — один черновик: прежний держатель этого слота его теряет.
            for (const [draftId, slotId] of next) if (slotId === o.slotId) next.delete(draftId);
            next.set(o.draftId, o.slotId);
          }
          return next;
        });
      }
      if (failed.length > 0) setError(`Не ушли на перерисовку даты: ${failed.join('; ')}`);
      backlog.refetch();
      schedule.refetch();
    });
  };

  const chatId = readyChatId(channel);

  const submit = (): void => {
    if (blockers.length > 0 || busy || mode === undefined || chatId === null) return;
    const payload = chosen.flatMap((card): BatchApproveItem[] => {
      const slot = assignment.get(card.item.draft_id);
      const model = card.model;
      if (model === null || slot === undefined) return [];
      return [
        {
          draft_id: card.item.draft_id,
          version_no: model.version_no,
          // Дословно из ответа расписания — 09 §9.4. Никакой сборки момента на клиенте.
          slot_at: slot.slot_at,
          expected_content_hash: model.content_hash,
        },
      ];
    });
    if (payload.length !== chosen.length) {
      setError('Пакет не собрался: у части постов нет версии или слота. Обновите экран.');
      return;
    }

    setBusy(true);
    setError(null);
    setResults(null);
    setBlocked(null);
    setSent(
      chosen.map((c) => ({
        draft_id: c.item.draft_id,
        title: titleOf(c.item),
        slot_local: assignment.get(c.item.draft_id)?.slot_local ?? null,
      })),
    );

    const body: BatchApproveBody = {
      items: payload,
      expected_mode: mode,
      expected_brand: brand,
      expected_chat_id: chatId,
    };
    post<unknown>('/api/v1/batch/approve', body)
      .then((body) => {
        const parsed = parseBatchResults(body);
        if (parsed === null) {
          setError('Ответ пакета не разобрался: обновите экран и проверьте состояние постов.');
          return;
        }
        setResults(parsed);
        setStartSlotId(null);
      })
      .catch((e: unknown) => {
        if (e instanceof ApiRequestError) {
          const list = parseBlocked(e.details);
          if (list !== null) setBlocked(list);
          else setError(e.message);
        } else {
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        setBusy(false);
        // Состояние на сервере изменилось при любом исходе: и при частичном успехе, и при
        // отказе предпроверки. Экран обязан перечитать бэклог и расписание, а не показывать
        // прежнюю картинку под свежим результатом. Режим и канал этого режима — вместе: режим
        // могли сменить в другой вкладке или другим админом (тогда `rev` этой шапки не растёт),
        // и свежий режим со старым каналом — это ровно `channel_mismatch` на каждом следующем
        // нажатии, до перезагрузки страницы.
        backlog.refetch();
        schedule.refetch();
        dispatch.refetch();
        refetchChannel();
      });
  };

  const toggle = (draftId: string): void => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(draftId)) next.delete(draftId);
      else next.add(draftId);
      return next;
    });
  };

  const move = (draftId: string, by: -1 | 1): void => {
    setOrder(() => {
      const ids = ordered.map((c) => c.item.draft_id);
      const at = ids.indexOf(draftId);
      const to = at + by;
      if (at < 0 || to < 0 || to >= ids.length) return ids;
      const next = [...ids];
      const a = next[at];
      const b = next[to];
      if (a === undefined || b === undefined) return ids;
      next[at] = b;
      next[to] = a;
      return next;
    });
  };

  const headline =
    backlog.error !== undefined
      ? 'бэклог не загрузился'
      : loading || backlog.data === undefined
        ? 'загрузка…'
        : `в пакет входят черновики, из статуса которых одобрение законно — ${chosen.length} из ${(backlog.data ?? []).length}`;

  let body: JSX.Element;
  if (backlog.error !== undefined) {
    body = (
      <div className={styles['state']}>
        <div className="val val-err" role="alert">
          <IconWarn />
          <span>Бэклог не загрузился: {backlog.error.message}</span>
        </div>
      </div>
    );
  } else if (loading || backlog.data === undefined) {
    body = (
      <div className={styles['state']}>
        <span className="micro">Загружаем черновики и их канонические версии…</span>
      </div>
    );
  } else if (ordered.length === 0) {
    body = (
      <div className={styles['state']}>
        <span className="h3">Пакет собирать не из чего</span>
        <span className="micro">
          Ни один черновик не находится в статусе, из которого одобрение законно: «ожидает»,
          «одобрен» или «не опубликован».
        </span>
      </div>
    );
  } else {
    body = (
      <div className={styles['grid']}>
        <div className={styles['cards']}>
          {ordered.map((card, i) => (
            <BatchCard
              key={card.item.draft_id}
              card={card}
              brand={brand}
              checked={checked.has(card.item.draft_id)}
              slot={assignment.get(card.item.draft_id) ?? null}
              first={i === 0}
              last={i === ordered.length - 1}
              onToggle={() => {
                toggle(card.item.draft_id);
              }}
              onMove={(by) => {
                move(card.item.draft_id, by);
              }}
            />
          ))}
          <div className="micro">
            Снятие галочки исключает пост из пакета и статус не меняет. Порядок карточек — это
            порядок раздачи слотов; двигайте его стрелками.
          </div>
        </div>

        <div className={styles['side']}>
          <div className={`card-flat ${styles['sideCard']}`}>
            <div className="h3">Слоты пакета</div>
            <div className="micro">
              Выберите первый слот — остальные свободные слоты расписания раздадутся выбранным
              постам по порядку очереди. Занятые слоты пропускаются: один слот занимает ровно один
              пост.
            </div>

            <SlotPicker
              brand={brand}
              selectedSlotId={startSlotId}
              onPick={(slot) => {
                setStartSlotId(slot.slot_id);
              }}
            />

            {chosen.length === 0 ? null : (
              <div className="stack" style={{ gap: 2 }}>
                {chosen.map((card) => {
                  const slot = assignment.get(card.item.draft_id) ?? null;
                  return (
                    <div key={card.item.draft_id} className="vrow">
                      <span
                        className={`mono num ${styles['queueSlot']} ${slot === null ? styles['queueSlotNone'] : ''}`}
                      >
                        {slot === null ? '—' : slotLabel(slot.slot_local)}
                      </span>
                      <span
                        className={`small ${styles['queueTitle']} ${slot === null ? styles['queueTitleNone'] : ''}`}
                      >
                        {titleOf(card.item)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {posterMismatches.length === 0 ? null : (
            <button
              type="button"
              className={redating ? 'btn btn-w btn-dis' : 'btn btn-ghost btn-w'}
              disabled={redating}
              onClick={redateAll}
              data-testid="batch-poster-redate"
            >
              {`Перерисовать даты обложек под слоты пакета (${String(posterMismatches.length)})`}
            </button>
          )}

          {heldAway.length === 0 ? null : (
            <div className="stack" style={{ gap: 4 }} data-testid="batch-poster-held">
              <span className="small">
                {`Ждут новой даты обложки: ${String(heldAway.length)}. Их слоты придержаны и другим постам пакета не достаются: ${heldAway.map((h) => slotLabel(h.slot_local)).join(', ')}. Писатель перерисовывает дату без модели, обычно за минуту; «Обновить» вернёт эти посты в пакет.`}
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-w"
                onClick={() => {
                  backlog.refetch();
                  schedule.refetch();
                }}
              >
                Обновить
              </button>
            </div>
          )}

          {blockers.length === 0 ? null : (
            <div className="val val-err" role="alert" data-testid="batch-blockers">
              <IconWarn />
              <span>
                Пакет не отправляется. Каждый пост проходит проверку отдельно, и одна блокирующая
                ошибка отменяет пакет целиком — ни один пост не будет одобрен.
                <br />
                {blockers.map((b) => (
                  <span key={b.key} style={{ display: 'block' }}>
                    {b.text}
                  </span>
                ))}
              </span>
            </div>
          )}

          {error === null ? null : (
            <div className="val val-err" role="alert">
              <IconWarn />
              <span>{error}</span>
            </div>
          )}

          {blocked === null ? null : (
            <div className="val val-err" role="alert" data-testid="batch-blocked">
              <IconWarn />
              <span>
                Бэкенд отклонил пакет на предпроверке — не одобрен ни один пост.
                <br />
                {blocked.map((b) => (
                  <span key={`${b.draft_id}:${b.code}`} style={{ display: 'block' }}>
                    «
                    {sent.find((s) => s.draft_id === b.draft_id)?.title ??
                      'пост не из этого пакета'}
                    » — {b.message} (код {b.code})
                  </span>
                ))}
              </span>
            </div>
          )}

          {mode === undefined ? null : (
            <div className={mode === 'live' ? 'val val-warn' : 'val val-info'}>
              <span>
                {mode === 'live'
                  ? 'Боевой режим: пакет уйдёт в боевой канал — посты увидят подписчики.'
                  : 'Тестовый режим: пакет уйдёт в тестовый канал. В боевой эти посты не попадут и после переключения режима.'}
                {channel.kind === 'ready' ? ` Канал: ${channel.channelId}.` : ''}
              </span>
            </div>
          )}

          <div className="stack" style={{ gap: 8 }}>
            <HoldToConfirm
              label={`утвердить пакет (${chosen.length})`}
              disabled={blockers.length > 0 || busy}
              onConfirm={submit}
            />
            <div className="micro" style={{ textAlign: 'center' }}>
              одно удержание 800 мс на весь пакет · каждый пост получает свой ключ на свой слот
            </div>
          </div>

          {results === null ? null : <ResultsPanel results={results} sent={sent} />}

          <div className="notice">
            Одобрение идёт по одному посту: сначала проверяются все, потом выпускаются ключи. На
            первом отказе пакет останавливается, а уже одобренные посты остаются одобренными —
            выпущенный ключ не отзывается задним числом.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['page']}>
      <div className={styles['head']}>
        <div className="stack" style={{ gap: 4 }}>
          <div className="h1">Пакет на неделю</div>
          <div className="micro">{headline}</div>
        </div>
        <div style={{ flex: 1 }} />
        <Link href={`/b/${encodeURIComponent(brand)}/backlog`} className="btn btn-ghost btn-sm">
          Назад в бэклог
        </Link>
      </div>
      {body}
    </div>
  );
}
